// Page-level Offline Wrapper Component
import React from 'react';
import { useOfflineMode } from '../hooks/useOfflineMode.js';
import { ReadOnlyModeBanner } from './atoms/ReadOnlyModeBanner.jsx';

/**
 * Wrapper component for pages that need offline/read-only mode handling
 * Shows appropriate banners and handles page-level offline state
 */
export function OfflinePageWrapper({
  children,
  showReadOnlyBanner = true,
  requiredData = [],
  fallbackContent = null,
  className = '',
}) {
  const { isOffline, readOnlyMode, capabilities } = useOfflineMode();

  // Check if required data is available
  const hasRequiredData =
    requiredData.length === 0 ||
    requiredData.every((dataType) => capabilities?.availableData?.includes(dataType));

  // Show fallback content if offline and missing required data
  if (isOffline && !hasRequiredData && fallbackContent) {
    return (
      <div className={className}>
        {showReadOnlyBanner && <ReadOnlyModeBanner />}
        {fallbackContent}
      </div>
    );
  }

  return (
    <div className={className}>
      {showReadOnlyBanner && readOnlyMode && <ReadOnlyModeBanner />}
      {children}
    </div>
  );
}

/**
 * Higher-order component that wraps a page component with offline handling
 */
export function withOfflinePage(WrappedComponent, options = {}) {
  const { showReadOnlyBanner = true, requiredData = [], fallbackContent = null } = options;

  return function OfflineWrappedPage(props) {
    return (
      <OfflinePageWrapper
        showReadOnlyBanner={showReadOnlyBanner}
        requiredData={requiredData}
        fallbackContent={fallbackContent}
      >
        <WrappedComponent {...props} />
      </OfflinePageWrapper>
    );
  };
}

/**
 * No Data Available component for offline pages
 */
export function OfflineNoDataFallback({ dataTypes = [], className = '' }) {
  return (
    <div className={`py-12 text-center ${className}`}>
      <div className="mx-auto max-w-md">
        <svg
          className="mx-auto mb-4 h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mb-2 text-lg font-medium text-gray-900">No Cached Data Available</h3>
        <p className="mb-4 text-gray-500">
          {dataTypes.length > 0 ? (
            <>This page requires {dataTypes.join(', ')} data which is not available offline.</>
          ) : (
            <>This page requires data that is not available offline.</>
          )}
        </p>
        <div className="flex items-center justify-center text-sm text-blue-600">
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
            />
          </svg>
          Connect to internet to view this page
        </div>
      </div>
    </div>
  );
}
